import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Tag, Table } from 'antd';
import { FileSpreadsheet, Clock, User, MapPin, UploadCloud } from 'lucide-react';

export default function ImportPreviewModal({
  isOpen,
  onClose,
  onConfirm,
  file,
  previewData = [],
  loading = false,
  isSubmitting = false
}) {
  const validCount = previewData.filter(item => item.isValid !== false).length;
  const invalidCount = previewData.length - validCount;
  const hasInvalidRows = invalidCount > 0;

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 50,
      align: 'center',
      render: (_, __, index) => <span className="text-gray-500 font-medium text-xs">{index + 1}</span>
    },
    {
      title: 'Ngày tiếp dân',
      dataIndex: 'ngayTiepDan',
      key: 'ngayTiepDan',
      width: 130,
      render: (text) => (
        <span className="font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded text-xs border border-blue-200">
          {text || '---'}
        </span>
      )
    },
    {
      title: 'Khung giờ',
      key: 'time',
      width: 140,
      render: (_, record) => (
        <div className="flex items-center gap-1 text-xs text-gray-800 font-medium">
          <Clock className="w-3.5 h-3.5 text-blue-600 shrink-0" />
          <span>{record.batDau || '??:??'} - {record.ketThuc || '??:??'}</span>
        </div>
      )
    },
    {
      title: 'Cán bộ tiếp dân',
      dataIndex: 'tenCanBo',
      key: 'tenCanBo',
      width: 170,
      render: (text) => (
        <div className="flex items-center gap-1 text-xs text-gray-800">
          <User className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
          <span className="truncate" title={text || 'Cán bộ tiếp dân'}>{text || 'Cán bộ tiếp dân'}</span>
        </div>
      )
    },
    {
      title: 'Địa điểm',
      dataIndex: 'diaDiem',
      key: 'diaDiem',
      width: 170,
      render: (text) => (
        <div className="flex items-center gap-1 text-xs text-gray-600">
          <MapPin className="w-3.5 h-3.5 text-rose-500 shrink-0" />
          <span className="truncate" title={text || 'Phòng tiếp công dân'}>{text || 'Phòng tiếp công dân'}</span>
        </div>
      )
    },
    {
      title: 'Ghi chú',
      dataIndex: 'ghiChu',
      key: 'ghiChu',
      ellipsis: true,
      render: (text) => (
        <span className="text-xs text-gray-500 truncate" title={text || '---'}>
          {text || <span className="italic text-gray-400">---</span>}
        </span>
      )
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 110,
      align: 'center',
      render: (_, record) => {
        if (record.isValid === false) {
          return (
            <Tag color="error" className="text-[11px] m-0">
              {record.error || 'Lỗi dữ liệu'}
            </Tag>
          );
        }
        return (
          <Tag color="success" className="text-[11px] m-0">
            Hợp lệ
          </Tag>
        );
      }
    }
  ];

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      width={900}
      centered
      title={
        <div className="flex items-center gap-2 text-gray-900 border-b border-gray-100 pb-3">
          <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
            <FileSpreadsheet className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900 m-0 leading-tight">
              Xác nhận import lịch tiếp công dân
            </h3>
            <p className="text-xs text-gray-500 font-normal m-0 mt-0.5">
              Vui lòng kiểm tra lại danh sách các ca tiếp dân được đọc từ file trước khi lưu
            </p>
          </div>
        </div>
      }
      footer={
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <div className="text-xs text-gray-500">
            Tổng cộng: <strong className="text-gray-900">{previewData.length}</strong> dòng |{' '}
            Hợp lệ: <strong className="text-emerald-600">{validCount}</strong>
            {invalidCount > 0 && (
              <> | Lỗi: <strong className="text-red-600">{invalidCount}</strong></>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs font-medium transition-colors cursor-pointer disabled:opacity-50"
            >
              Hủy bỏ
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isSubmitting || previewData.length === 0 || hasInvalidRows}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-xs font-semibold flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer disabled:opacity-50"
            >
              <UploadCloud className="w-4 h-4" />
              <span>{isSubmitting
                ? 'Đang lưu vào hệ thống...'
                : hasInvalidRows
                  ? 'Sửa dữ liệu lỗi trong file để tiếp tục'
                  : `Xác nhận Import (${validCount} lịch)`}</span>
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-4 my-2">
        {/* FILE INFO BANNER */}
        {file && (
          <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between text-xs">
            <div className="flex items-center gap-2 min-w-0">
              <FileSpreadsheet className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="font-semibold text-gray-900 truncate max-w-sm" title={file.name}>
                {file.name}
              </span>
              <span className="text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 font-medium">
                {previewData.length} lịch tiếp dân
              </span>
            </div>
          </div>
        )}

        {/* PREVIEW TABLE */}
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <Table
            dataSource={previewData.map((item, idx) => ({ ...item, key: idx }))}
            columns={columns}
            pagination={{ pageSize: 5, size: 'small', showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} lịch` }}
            size="small"
            loading={loading}
            scroll={{ y: 280 }}
            locale={{
              emptyText: (
                <div className="py-6 text-center text-gray-400 text-xs">
                  Không tìm thấy dữ liệu hợp lệ trong file Excel.
                </div>
              )
            }}
          />
        </div>
      </div>
    </Modal>
  );
}

ImportPreviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  file: PropTypes.object,
  previewData: PropTypes.array,
  loading: PropTypes.bool,
  isSubmitting: PropTypes.bool,
};
